// ไฟล์: frontend/src/pages/NewsDetailPage.js
import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import './NewsDetailPage.css';

const NewsDetailPage = ({ user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [news, setNews] = useState(null);
  const [relatedNews, setRelatedNews] = useState([]);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNewsDetail = async () => {
      try {
        const response = await axios.get(`/news/${id}`);
        setNews(response.data.news);
        setComments(response.data.comments || []);
        
        // ดึงข่าวที่เกี่ยวข้อง
        const relatedResponse = await axios.get(`/news?category_id=${response.data.news.category_id}`);
        // กรองข่าวปัจจุบันออก และจำกัดจำนวน 3 รายการ
        const filtered = relatedResponse.data.news
          .filter(item => item.id !== parseInt(id))
          .slice(0, 3);
        
        setRelatedNews(filtered);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching news detail:', error);
        setLoading(false);
        // กรณีไม่พบข่าว
        if (error.response && error.response.status === 404) {
          navigate('/news');
        }
      }
    };

    fetchNewsDetail();
    // ให้เลื่อนไปด้านบนของหน้าเมื่อโหลดข้อมูลใหม่
    window.scrollTo(0, 0);
  }, [id, navigate]);

  // ฟังก์ชันแปลงวันที่เป็นรูปแบบไทย
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString('th-TH', options);
  };

  // ฟังก์ชันเพิ่มความคิดเห็น
  const handleAddComment = async (e) => {
    e.preventDefault();
    
    if (!user) {
      Swal.fire({
        title: 'กรุณาเข้าสู่ระบบ',
        text: 'คุณต้องเข้าสู่ระบบก่อนแสดงความคิดเห็น',
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: 'เข้าสู่ระบบ',
        cancelButtonText: 'ยกเลิก'
      }).then((result) => {
        if (result.isConfirmed) {
          navigate('/login');
        }
      });
      return;
    }
    
    if (!commentText.trim()) {
      Swal.fire({
        title: 'แจ้งเตือน',
        text: 'กรุณากรอกข้อความแสดงความคิดเห็น',
        icon: 'warning',
        confirmButtonText: 'ตกลง'
      });
      return;
    }
    
    try {
      const response = await axios.post('/comments', {
        content: commentText,
        news_id: id
      });
      
      setComments([response.data.comment, ...comments]);
      setCommentText('');
      
      Swal.fire({
        title: 'สำเร็จ',
        text: 'แสดงความคิดเห็นเรียบร้อยแล้ว',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Error adding comment:', error);
      Swal.fire({
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถแสดงความคิดเห็นได้ โปรดลองอีกครั้ง',
        icon: 'error',
        confirmButtonText: 'ตกลง'
      });
    }
  };

  // ฟังก์ชันลบความคิดเห็น
  const handleDeleteComment = async (commentId) => {
    Swal.fire({
      title: 'ยืนยันการลบ',
      text: 'คุณต้องการลบความคิดเห็นนี้ใช่หรือไม่?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'ลบ',
      cancelButtonText: 'ยกเลิก'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`/comments/${commentId}`);
          setComments(comments.filter(comment => comment.id !== commentId));
          
          Swal.fire({
            title: 'สำเร็จ',
            text: 'ลบความคิดเห็นเรียบร้อยแล้ว',
            icon: 'success',
            timer: 1500,
            showConfirmButton: false
          });
        } catch (error) {
          console.error('Error deleting comment:', error);
          Swal.fire({
            title: 'เกิดข้อผิดพลาด',
            text: 'ไม่สามารถลบความคิดเห็นได้ โปรดลองอีกครั้ง',
            icon: 'error',
            confirmButtonText: 'ตกลง'
          });
        }
      }
    });
  };

  // ฟังก์ชันแชร์ไปยังโซเชียลมีเดีย
  const handleShare = (platform) => {
    const url = window.location.href;
    const title = news?.title || 'ข่าวสารการศึกษา';
    
    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`, '_blank');
        break;
      case 'line':
        window.open(`https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(url)}`, '_blank');
        break;
      default:
        break;
    }
  };

  // ฟังก์ชันบันทึกลงคลังส่วนตัว
  const handleSaveToLibrary = async () => {
    if (!user) {
      Swal.fire({
        title: 'กรุณาเข้าสู่ระบบ',
        text: 'คุณต้องเข้าสู่ระบบก่อนบันทึกลงคลังส่วนตัว',
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: 'เข้าสู่ระบบ',
        cancelButtonText: 'ยกเลิก'
      }).then((result) => {
        if (result.isConfirmed) {
          navigate('/login');
        }
      });
      return;
    }
    
    try {
      await axios.post('/library', {
        news_id: id,
        status: 'saved',
        collection_name: 'ข่าวสาร'
      });
      
      Swal.fire({
        title: 'บันทึกสำเร็จ',
        text: 'บันทึกข่าวลงคลังส่วนตัวเรียบร้อยแล้ว',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Error saving to library:', error);
      
      // กรณีบันทึกซ้ำ
      if (error.response && error.response.data.message.includes('อัพเดทรายการในคลังสำเร็จ')) {
        Swal.fire({
          title: 'บันทึกสำเร็จ',
          text: 'อัพเดทรายการในคลังเรียบร้อยแล้ว',
          icon: 'success',
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        Swal.fire({
          title: 'เกิดข้อผิดพลาด',
          text: 'ไม่สามารถบันทึกลงคลังส่วนตัวได้ โปรดลองอีกครั้ง',
          icon: 'error',
          confirmButtonText: 'ตกลง'
        });
      }
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        {/* <div className="loading-spinner">กำลังโหลดข้อมูล...</div> */}
      </div>
    );
  }

  if (!news) {
    return (
      <div className="container">
        <div className="error-state">
          <div className="error-icon">
            <i className="fas fa-exclamation-circle"></i>
          </div>
          <h2>ไม่พบข่าวที่ต้องการ</h2>
          <p>ข่าวที่คุณต้องการอาจถูกลบไปแล้วหรือไม่มีอยู่ในระบบ</p>
          <Link to="/news" className="btn btn-primary">
            <i className="fas fa-arrow-left"></i> กลับไปหน้าข่าวทั้งหมด
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="news-detail-page">
      <div className="container">
        {/* Breadcrumb */}
        <div className="breadcrumb">
          <Link to="/">หน้าหลัก</Link>
          <i className="fas fa-chevron-right"></i>
          <Link to="/news">ข่าวสารการศึกษา</Link>
          <i className="fas fa-chevron-right"></i>
          <span>{news.title}</span>
        </div>

        <div className="news-detail-container">
          {/* Main Content */}
          <div className="news-detail-main">
            <div className="news-detail-header">
              <h1 className="news-detail-title">{news.title}</h1>
              <div className="news-detail-meta">
                <div className="news-detail-author">
                  <i className="fas fa-user"></i> {news.author_name}
                </div>
                <div className="news-detail-date">
                  <i className="far fa-calendar-alt"></i> {formatDate(news.created_at)}
                </div>
                <div className="news-detail-category">
                  <i className="fas fa-folder"></i> {news.category_name}
                </div>
                <div className="news-detail-views">
                  <i className="far fa-eye"></i> {news.view_count} ครั้ง
                </div>
              </div>
            </div>

            {/* Thumbnail */}
            <div className="news-detail-thumbnail">
              <img 
                src={news.thumbnail 
                  ? `${process.env.REACT_APP_API_URL}/uploads/${news.thumbnail}`
                  : '/assets/default-news.jpg'} 
                alt={news.title} 
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/assets/default-news.jpg';
                }}
              />
            </div>

            {/* Content */}
            <div className="news-detail-content">
              <div dangerouslySetInnerHTML={{ __html: news.content }} />
            </div>

            {/* Actions */}
            <div className="news-detail-actions">
              <div className="social-share">
                <span>แชร์:</span>
                <button 
                  className="btn-share facebook" 
                  onClick={() => handleShare('facebook')}
                >
                  <i className="fab fa-facebook-f"></i>
                </button>
                <button 
                  className="btn-share twitter" 
                  onClick={() => handleShare('twitter')}
                >
                  <i className="fab fa-twitter"></i>
                </button>
                <button 
                  className="btn-share line" 
                  onClick={() => handleShare('line')}
                >
                  <i className="fab fa-line"></i>
                </button>
              </div>
              <button 
                className="btn btn-primary" 
                onClick={handleSaveToLibrary}
              >
                <i className="fas fa-bookmark"></i> บันทึกลงคลัง
              </button>
            </div>

            {/* Comments Section */}
            <div className="comments-section">
              <h3 className="comments-title">
                <i className="fas fa-comments"></i> ความคิดเห็น ({comments.length})
              </h3>
              
              {/* Comment Form */}
              <div className="comment-form-container">
                <form onSubmit={handleAddComment} className="comment-form">
                  <textarea
                    placeholder="แสดงความคิดเห็นของคุณ..."
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    disabled={!user}
                    className="form-control"
                    rows={3}
                  ></textarea>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={!user}
                  >
                    <i className="fas fa-paper-plane"></i> ส่งความคิดเห็น
                  </button>
                </form>
                {!user && (
                  <div className="comment-login-message">
                    <Link to="/login">เข้าสู่ระบบ</Link> เพื่อแสดงความคิดเห็น
                  </div>
                )}
              </div>
              
              {/* Comments List */}
              <div className="comments-list">
                {comments.length > 0 ? (
                  comments.map(comment => (
                    <div className="comment-item" key={comment.id}>
                      <div className="comment-avatar">
                        <img 
                          src={comment.profile_image 
                            ? `${process.env.REACT_APP_API_URL}/uploads/profiles/${comment.profile_image}`
                            : `https://ui-avatars.com/api/?name=${comment.fullname}&background=random`} 
                          alt={comment.fullname} 
                        />
                      </div>
                      <div className="comment-content">
                        <div className="comment-header">
                          <div className="comment-author">{comment.fullname}</div>
                          <div className="comment-date">{formatDate(comment.created_at)}</div>
                        </div>
                        <div className="comment-text">{comment.content}</div>
                        {(user && (user.id === comment.user_id || user.role === 'admin')) && (
                          <div className="comment-actions">
                            <button 
                              className="btn-link text-danger" 
                              onClick={() => handleDeleteComment(comment.id)}
                            >
                              <i className="fas fa-trash-alt"></i> ลบ
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-comments">
                    <div className="no-comments-icon">
                      <i className="far fa-comment-dots"></i>
                    </div>
                    <p>ยังไม่มีความคิดเห็น เป็นคนแรกที่แสดงความคิดเห็น</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="news-detail-sidebar">
            {/* Related News */}
            <div className="sidebar-card">
              <h3 className="sidebar-card-title">
                <i className="fas fa-newspaper"></i> ข่าวที่เกี่ยวข้อง
              </h3>
              {relatedNews.length > 0 ? (
                <div className="related-news-list">
                  {relatedNews.map(item => (
                    <div className="related-news-item" key={item.id}>
                      <div className="related-news-image">
                        <Link to={`/news/${item.id}`}>
                          <img 
                            src={item.thumbnail 
                              ? `${process.env.REACT_APP_API_URL}/uploads/${item.thumbnail}`
                              : '/assets/default-news.jpg'} 
                            alt={item.title} 
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = '/assets/default-news.jpg';
                            }}
                          />
                        </Link>
                      </div>
                      <div className="related-news-info">
                        <h4 className="related-news-title">
                          <Link to={`/news/${item.id}`}>{item.title}</Link>
                        </h4>
                        <div className="related-news-date">
                          <i className="far fa-calendar-alt"></i> {formatDate(item.created_at)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-related-news">
                  <p>ไม่พบข่าวที่เกี่ยวข้อง</p>
                </div>
              )}
              <Link to="/news" className="btn btn-outline btn-sm btn-block mt-3">
                <i className="fas fa-list"></i> ดูข่าวทั้งหมด
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsDetailPage;
